import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { encodeParam } from "Utilities/urlEncoder";
import { useSearchAnalytics } from "Context/SearchAnalyticsContext";
import { useLazyGetListOfBookProductsWithFilterQuery } from "Services/ProductApiSlice";

interface SimpleListDropdownProps {
  style: React.CSSProperties;
  items: string[];
  type: "language" | "academic" | "merchandise";
  onMouseLeave: () => void;
}

const ITEM_HEIGHT = 32;
const MAX_VISIBLE_ITEMS = 8;

const SimpleListDropdown: React.FC<SimpleListDropdownProps> = ({
  style,
  items,
  type,
  onMouseLeave,
}) => {
  const navigate = useNavigate();
  const { queueAnalytics, resolveAnalytics } = useSearchAnalytics();
  const [inputValue, setInputValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const filteredItems = useMemo(() => {
    return items.filter((item) =>
      item.toLowerCase().includes(appliedSearch.toLowerCase())
    );
  }, [items, appliedSearch]);

  const [triggerGetProducts] = useLazyGetListOfBookProductsWithFilterQuery();

  const handleSubmit = async () => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      const matches = items.filter((item) =>
        item.toLowerCase().includes(trimmed.toLowerCase())
      );
      if (matches.length === 0) {
        queueAnalytics(type.toUpperCase(), trimmed);
        resolveAnalytics(false);
      } else {
        const bestMatch = matches[0];
        try {
          const result = await triggerGetProducts({
            [type]: bestMatch,
            mode: "CRITERIA",
            page: 1,
            productsCount: 1,
          }).unwrap();
          const products = (result as any)?.results?.products || [];
          queueAnalytics(type.toUpperCase(), bestMatch);
          resolveAnalytics(products.length > 0);
        } catch (error) {
          console.error("Error verifying product availability:", error);
          queueAnalytics(type.toUpperCase(), bestMatch);
          resolveAnalytics(false);
        }
      }
    }
    setAppliedSearch(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!filteredItems.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < filteredItems.length - 1 ? prev + 1 : prev
      );
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
    }

    if (e.key === "Enter") {
      handleItemClick(filteredItems[activeIndex]);
    }

    if (e.key === "Escape") {
      onMouseLeave();
    }
  };

  const handleItemClick = (item: string) => {
    queueAnalytics(type.toUpperCase(), item);
    navigate(`/books-listing?${type}=${encodeParam(item)}`);
  };

  return (
    <>
      <DropdownContainer style={style} onMouseLeave={onMouseLeave}>
        <div className="p-3 w-56">
          <div className="relative w-full mb-3">
            <input
              type="text"
              placeholder={`Search ${type}...`}
              value={inputValue}
              onChange={(e) => {
                const val = e.target.value;
                setInputValue(val);
                if (!val.trim()) {
                  setAppliedSearch("");
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSubmit();
                } else {
                  handleKeyDown(e);
                }
              }}
              className="w-full pr-8 px-2 py-1 text-sm border rounded-md focus:outline-none"
            />
            <div
              className="absolute right-2 top-0 h-full flex items-center text-gray-700 text-opacity-50 cursor-pointer hover:text-vintageText transition-colors"
              onClick={handleSubmit}
              tabIndex={0}
              role="button"
              aria-label="Search"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleSubmit();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-search"
              >
                <path d="m21 21-4.34-4.34" />
                <circle cx="11" cy="11" r="8" />
              </svg>
            </div>
          </div>

          {filteredItems.length === 0 ? (
            <p className="text-sm text-gray-500">No results found</p>
          ) : (
            <ul
              ref={listRef}
              className="space-y-1 overflow-y-auto hide-scrollbar"
              style={{ maxHeight: `${ITEM_HEIGHT * MAX_VISIBLE_ITEMS}px` }}
            >
              {filteredItems.map((item, index) => (
                <li
                  key={index}
                  onClick={() => handleItemClick(item)}
                  className={`px-2 py-1 text-sm cursor-pointer rounded-md ${
                    index === activeIndex
                      ? "bg-vintageText bg-opacity-10"
                      : "hover:bg-vintageText hover:bg-opacity-5"
                  }`}
                >
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DropdownContainer>


      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
    </>
  );
};

const DropdownContainer: React.FC<{
  style: React.CSSProperties;
  onMouseLeave: () => void;
  children: React.ReactNode;
}> = ({ style, onMouseLeave, children }) => (
  <div
    className="absolute top-full mt-0 bg-vintageBg bg-opacity-95 rounded-2xl z-50 shadow-lg"
    style={style}
    onMouseLeave={onMouseLeave}
  >
    {children}
  </div>
);

export default SimpleListDropdown;